import type { FetchMatcher, MatcherFixture } from 'tio-data/demo/types';
import { camelize } from '@ember/string';
import type { EnhancedSearchField } from 'tio-reporting/utils/field-filter-pipeline';
import { getEssentialFields } from 'tio-reporting/utils/dataset-utils';
import { getDemoTaProgramsForClient } from 'tio-employee/utils/career-pathways-demo-data';
import { matchesSearchEndpoint, type DemoFixtureIdentity } from './types';

const field = (
  name: string,
  label: string,
  type = 'keyword',
  currencyCents = false
): EnhancedSearchField => ({
  name,
  path: name,
  resultKey: name,
  label,
  type,
  currencyCents,
  isDisplayable: true,
  isSortable: true,
  isAggregatable: true,
});

const DATASETS = {
  'demo-report-tas': {
    modelType: 'TasApplication',
    companyFilterField: 'company_id_keyword',
    fields: [
      field('employee_name_keyword', 'Employee'),
      field('employee_payroll_id_keyword', 'Employee ID'),
      field('tas_application_id_keyword', 'Application'),
      field('tas_application_state_keyword', 'Status'),
      field('tas_application_created_at_date', 'Submitted', 'date'),
    ],
  },
  'demo-report-tas-programs': {
    modelType: 'TasProgramInstance',
    companyFilterField: 'company_id_keyword',
    fields: [
      field('employee_name_keyword', 'Employee'),
      field('employee_payroll_id_keyword', 'Employee ID'),
      field('tas_program_instance_id_keyword', 'Program ID'),
      field('tas_program_template_field_program_name_keyword', 'Program'),
      field('tas_program_instance_field_school_institution_name_keyword', 'Institution'),
      field('tas_program_instance_state_keyword', 'Program Status'),
      field('tas_program_instance_created_at_date', 'Created', 'date'),
      field('tas_application_id_keyword', 'Application'),
      field('tas_application_state_keyword', 'Application Status'),
      field('tas_application_created_at_date', 'Submitted', 'date'),
    ],
  },
  'demo-report-slr': {
    modelType: 'AccountingTransaction',
    companyFilterField: 'slr_transaction_company_id_keyword',
    fields: [
      field('slr_transaction_employee_name_keyword', 'Employee'),
      field('slr_transaction_transaction_date_date', 'Payment Date', 'date'),
      field('slr_transaction_effective_period_keyword', 'Benefit Month'),
      field('slr_transaction_amount_integer', 'Contribution', 'integer', true),
      field('slr_transaction_servicer_name_keyword', 'Servicer'),
      field('slr_transaction_state_keyword', 'Status'),
    ],
  },
  'demo-report-pslf': {
    modelType: 'PslfForm',
    companyFilterField: 'pslf_form_company_id_keyword',
    fields: [
      field('pslf_form_borrower_name_keyword_analyzable', 'Employee'),
      field('pslf_form_employer_name_keyword_analyzable', 'Employer'),
      field('pslf_form_status_keyword', 'Status'),
      field('pslf_form_created_at_date', 'Created', 'date'),
    ],
  },
  'demo-report-core': {
    modelType: 'Person',
    companyFilterField: 'company_ids',
    fields: [
      field('person_first_name_text', 'First Name', 'text'),
      field('person_last_name_text', 'Last Name', 'text'),
      field('all_employee_payroll_ids_text', 'Employee ID', 'text'),
      field('all_company_names_keyword', 'Employer'),
      field('person_is_enrolled_boolean', 'Enrolled', 'boolean'),
    ],
  },
  'demo-report-wellness': {
    modelType: 'Recommendation',
    companyFilterField: 'all_company_ids_keyword',
    fields: [
      field('person_first_name_text', 'First Name', 'text'),
      field('person_last_name_text', 'Last Name', 'text'),
      field('recommendation_requesting_app_keyword', 'Loan Planning Tool'),
      field('recommendation_started_on_date', 'Started', 'date'),
      field('recommendation_completed_boolean', 'Completed', 'boolean'),
    ],
  },
} satisfies Record<
  string,
  { modelType: string; companyFilterField: string; fields: EnhancedSearchField[] }
>;

const datasetFor = (id: string) => DATASETS[id as keyof typeof DATASETS];

const DATASET_CATALOG = {
  data: {
    attributes: {
      result: {
        availableDatasets: [
          {
            slug: 'tas-applications',
            label: 'Tuition Assistance Applications',
            modelType: 'TasApplication',
            product: 'TAS',
          },
          {
            slug: 'tas-program-instances',
            label: 'Tuition Assistance Programs',
            modelType: 'TasProgramInstance',
            product: 'TAS',
          },
          {
            slug: 'slr-transactions',
            label: 'SLR Contributions',
            modelType: 'AccountingTransaction',
            product: 'SLR',
          },
          { slug: 'pslf-forms', label: 'PSLF Forms', modelType: 'PslfForm', product: 'PSLF' },
          { slug: 'people', label: 'Employees', modelType: 'Person', product: 'Core' },
          {
            slug: 'recommendations',
            label: 'Financial Wellness',
            modelType: 'Recommendation',
            product: 'STRATEGYFINDER',
          },
        ],
      },
    },
  },
};

/** Saved viewer configuration for one listed demo report, scoped to the selected employer. */
export function buildDemoReportSearchConfig(id: string, identity: DemoFixtureIdentity) {
  const dataset = datasetFor(id) ?? DATASETS['demo-report-tas'];
  return {
    modelType: dataset.modelType,
    companyId: 'demo-tas-company-1',
    viewType: 'table',
    tableSettings: { selectedColumns: dataset.fields.map((column) => column.name) },
    employerName: identity.employerName,
  };
}

function fieldsForUrl(url: string) {
  const modelType = new URL(url, 'https://demo.invalid').searchParams.get('model-type');
  const dataset = Object.values(DATASETS).find((entry) => entry.modelType === modelType);
  return dataset
    ? {
        fields: dataset.fields,
        datasetConfig: {
          companyFilterField: dataset.companyFilterField,
          defaultColumns: dataset.fields.map((column) => column.name),
        },
      }
    : { fields: [] };
}

function reportRows(identity: DemoFixtureIdentity, modelType: string): Record<string, unknown>[] {
  const people = [
    ['Jordan', 'Lee', 'EMP-1001'],
    ['Priya', 'Natarajan', 'EMP-1042'],
    ['Marcus', 'Brown', 'EMP-1088'],
    ['Sofia', 'Alvarez', 'EMP-1112'],
  ];
  if (modelType === 'AccountingTransaction') {
    return ['2026-05-14', '2026-04-14', '2026-03-14'].flatMap((date, month) =>
      people
        .filter(([first]) => first !== 'Marcus')
        .map(([first, last, payroll], index) => ({
          id: `demo-slr-report-${month}-${payroll}`,
          slr_transaction_company_id_keyword: 'demo-tas-company-1',
          slr_transaction_employee_name_keyword: `${first} ${last}`,
          slr_transaction_transaction_date_date: date,
          slr_transaction_effective_period_keyword: date.slice(0, 7),
          slr_transaction_amount_integer: index === 2 ? 10000 : 12500,
          slr_transaction_servicer_name_keyword: index === 1 ? 'Nelnet' : 'MOHELA',
          slr_transaction_state_keyword: 'sent',
        }))
    );
  }
  return people
    .map(([first, last, payroll], index) => {
      const name = `${first} ${last}`;
      if (modelType === 'TasApplication' || modelType === 'TasProgramInstance') {
        return {
          id: `demo-queue-${index + 1}-application`,
          company_id_keyword: 'demo-tas-company-1',
          employee_name_keyword: name,
          employee_payroll_id_keyword: payroll,
          tas_application_id_keyword: `demo-queue-${index + 1}-application`,
          tas_application_state_keyword:
            index === 2
              ? 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
              : 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          tas_application_created_at_date: `2026-05-${20 + index * 2}`,
          ...(modelType === 'TasProgramInstance'
            ? {
                id: `demo-queue-${index + 1}-instance`,
                company_name_keyword: identity.employerName,
                employee_id_keyword: payroll?.slice(4),
                tas_program_instance_id_keyword: `demo-queue-${index + 1}-instance`,
                tas_program_template_id_keyword: 'demo-tas-template-1',
                tas_program_template_field_program_name_keyword: getDemoTaProgramsForClient(
                  identity.clientId
                )[0]?.name,
                tas_program_instance_field_school_institution_name_keyword:
                  'University of North Carolina at Chapel Hill',
                tas_program_instance_state_keyword: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
                tas_program_instance_created_at_date: `2026-05-${20 + index * 2}`,
              }
            : {}),
        };
      }
      if (modelType === 'PslfForm') {
        return {
          id: `demo-pslf-report-${index}`,
          pslf_form_company_id_keyword: 'demo-tas-company-1',
          pslf_form_borrower_name_keyword_analyzable: name,
          pslf_form_employer_name_keyword_analyzable: identity.employerName,
          pslf_form_status_keyword: [
            'PENDING_EMPLOYER_SIGNATURE',
            'SUBMITTED',
            'COMPLETE',
            'DRAFT',
          ][index],
          pslf_form_created_at_date: `2026-05-${10 + index}`,
        };
      }
      if (modelType === 'Recommendation') {
        return {
          id: `demo-wellness-report-${index}`,
          all_company_ids_keyword: ['demo-tas-company-1'],
          person_first_name_text: first,
          person_last_name_text: last,
          recommendation_requesting_app_keyword: index % 2 ? 'SF-0726' : 'IDR-0726',
          recommendation_started_on_date: `2026-05-${10 + index}`,
          recommendation_completed_boolean: index !== 2,
        };
      }
      return {
        id: `demo-person-report-${index}`,
        company_ids: ['demo-tas-company-1'],
        person_first_name_text: first,
        person_last_name_text: last,
        all_employee_payroll_ids_text: payroll,
        all_company_names_keyword: identity.employerName,
        person_is_enrolled_boolean: index !== 2,
      };
    })
    .slice(0, ['TasApplication', 'TasProgramInstance'].includes(modelType) ? 3 : people.length);
}

interface ReportSearchBody {
  from?: number;
  size?: number;
  sort?: unknown[];
  _source?: unknown;
  aggs?: { employees?: { terms?: { field?: string } } };
}

const requestBody = (body: unknown) =>
  body as
    | {
        'model-type'?: string;
        searchBody?: ReportSearchBody;
        'search-body'?: ReportSearchBody;
      }
    | undefined;
const matchesReportSearch = (body: unknown) => {
  const parsed = requestBody(body);
  const query = parsed?.searchBody ?? parsed?.['search-body'];
  const source = query?._source;
  const dataset = Object.values(DATASETS).find(
    (entry) => entry.modelType === parsed?.['model-type']
  );
  if (
    dataset?.modelType === 'TasProgramInstance' &&
    query?.size === 0 &&
    query.aggs?.employees?.terms?.field === 'tas_program_instance_id_keyword'
  ) {
    return true;
  }
  const fields = [
    ...(dataset?.fields.map((column) => column.name) ?? []),
    ...(dataset?.modelType === 'TasProgramInstance'
      ? getEssentialFields('tas-program-instances')
      : []),
  ];
  return (
    Array.isArray(source) &&
    source.length > 0 &&
    source.every((path) => typeof path === 'string' && fields.includes(path))
  );
};

function searchResponse(identity: DemoFixtureIdentity, body: unknown) {
  const parsed = requestBody(body);
  const rows = reportRows(identity, parsed?.['model-type'] ?? 'Person');
  const query = parsed?.searchBody ?? parsed?.['search-body'];
  for (const sort of [...(query?.sort ?? [])].reverse()) {
    if (!sort || typeof sort !== 'object') continue;
    const [entry] = Object.entries(sort);
    if (!entry) continue;
    const [key, value] = entry;
    const direction = typeof value === 'string' ? value : (value as { order?: string })?.order;
    rows.sort(
      (left, right) =>
        String(left[key] ?? '').localeCompare(String(right[key] ?? ''), undefined, {
          numeric: true,
        }) * (direction === 'desc' ? -1 : 1)
    );
  }
  const from = query?.from ?? 0;
  return {
    took: 0,
    timedOut: false,
    hits: {
      total: { value: rows.length, relation: 'eq' },
      hits: rows
        .slice(from, from + (query?.size ?? 50))
        .map((row) => ({ _id: row['id'], _score: 1, _source: row })),
    },
    aggregations:
      parsed?.['model-type'] === 'TasProgramInstance' && query?.aggs?.employees
        ? {
            employees: {
              buckets: rows.map((row) => ({
                key: row['tas_program_instance_id_keyword'],
                docCount: 1,
                applicationStates: {
                  buckets: [{ key: row['tas_application_state_keyword'], docCount: 1 }],
                },
                sampleDocument: {
                  hits: {
                    hits: [
                      {
                        _id: row['id'],
                        _source: Object.fromEntries(
                          Object.entries(row).map(([key, value]) => [camelize(key), value])
                        ),
                      },
                    ],
                  },
                },
              })),
            },
          }
        : {},
  };
}

/** Employer report metadata and row reads; register after the directory matcher and before empty search fallbacks. */
export function buildDemoReportMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  return [
    {
      matcher: { kind: 'builder', method: 'GET', urlIncludes: '/api/v2/search/datasets' },
      content: DATASET_CATALOG,
    },
    ...Object.values(DATASETS).map((dataset): MatcherFixture => ({
      matcher: {
        kind: 'builder',
        method: 'GET',
        urlMatches: (url) =>
          /\/api\/v2\/search\/fields(?:\?|$)/.test(url) &&
          new URL(url, 'https://demo.invalid').searchParams.get('model-type') === dataset.modelType,
      },
      content: {
        fields: dataset.fields,
        datasetConfig: {
          companyFilterField: dataset.companyFilterField,
          defaultColumns: dataset.fields.map((column) => column.name),
        },
      },
    })),
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: matchesSearchEndpoint,
        bodyMatches: matchesReportSearch,
      },
      contentFor: (body) => searchResponse(identity, body),
    },
  ];
}

/** Fetch twins for employer report metadata and rows when a viewer read bypasses the request manager. */
export function buildDemoReportFetchMatchers(identity: DemoFixtureIdentity): FetchMatcher[] {
  return [
    { method: 'GET', urlIncludes: '/api/v2/search/datasets', json: DATASET_CATALOG },
    { method: 'GET', urlIncludes: '/api/v2/sql-datasets', json: { data: [] } },
    ...(['POST', 'PATCH'] as const).map((method): FetchMatcher => ({
      method,
      urlMatches: (url) =>
        method === 'POST'
          ? /\/api\/v2\/report-trackings(?:\?|$)/.test(url)
          : /\/api\/v2\/report-trackings\/demo-report-tracking-[^/?]+(?:\?|$)/.test(url),
      status: method === 'POST' ? 201 : 200,
      json: (url: string, body?: unknown) => ({
        data: {
          type: 'report-tracking',
          id:
            method === 'POST'
              ? `demo-report-tracking-${crypto.randomUUID()}`
              : /\/report-trackings\/([^/?]+)/.exec(url)?.[1],
          attributes:
            (body as { data?: { attributes?: Record<string, unknown> } } | undefined)?.data
              ?.attributes ?? {},
        },
      }),
    })),
    {
      method: 'GET',
      urlMatches: (url) => /\/api\/v2\/search\/fields(?:\?|$)/.test(url),
      json: fieldsForUrl,
    },
    {
      method: 'POST',
      urlMatches: matchesSearchEndpoint,
      bodyMatches: matchesReportSearch,
      json: (_url: string, body?: unknown) => searchResponse(identity, body),
    },
  ];
}
